"use client";

import { useLayoutEffect, useRef, useState, type FormEvent } from "react";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import type { PlaceResult } from "@/lib/api/misc";
import { apiRequest } from "@/lib/api/_fetch";

import { prashnaLabels, type PrashnaLabels } from "./PrashnaCopy";
import { PrashnaFollowUpCTA } from "./PrashnaFollowUpCTA";
import PrashnaQuickQuestions from "./PrashnaQuickQuestions";
import {
  PrashnaCastingStatus,
  PrashnaReserveError,
  PrashnaResultReserve,
} from "./PrashnaResultReserve";

interface PrashnaResult {
  id: string;
  prashna_lagna: string;
  ruling_planets: string[];
  mode: string;
  answer_direction: string;
  confidence_score: number;
  hora_lord: string;
  moon_rashi: string;
  moon_nakshatra: string;
  question_category: { name_en: string; emoji: string };
  prediction_summary: string;
  house_analysis: Array<{
    house_number: number;
    sign: string;
    significance: string;
    is_strong: boolean;
  }>;
}

interface Props {
  locale?: "en" | "hi";
}

interface DirectionMeta {
  label: string;
  textClassName: string;
}

const DIRECTION_LABEL: Record<string, DirectionMeta> = {
  positive: { label: "Favourable", textClassName: "text-success" },
  negative: { label: "Challenging", textClassName: "text-error" },
  mixed: { label: "Mixed", textClassName: "text-warning" },
};

const DIRECTION_LABEL_HI: Record<string, DirectionMeta> = {
  positive: { label: "अनुकूल", textClassName: "text-success" },
  negative: { label: "प्रतिकूल", textClassName: "text-error" },
  mixed: { label: "मिश्रित", textClassName: "text-warning" },
};

export default function PrashnaClient({ locale = "en" }: Props) {
  const isHi = locale === "hi";

  const t = prashnaLabels(isHi);

  // Keep the high-frequency question value out of React state. Cloudflare RUM
  // identified this textarea as the persistent mobile INP owner: putting every
  // keystroke in the page-root state rerendered the location autocomplete and
  // any prior result tree. The DOM owns the text; React only observes the
  // empty/non-empty transition needed by the submit affordance.
  const questionRef = useRef("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [hasQuestion, setHasQuestion] = useState(false);
  const [locationText, setLocationText] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PrashnaResult | null>(null);
  // Counts accepted submits. Non-zero mounts the full-viewport result reserve;
  // each increment re-anchors it (a second question is a new submit).
  const [submitSequence, setSubmitSequence] = useState(0);
  const reserveRef = useRef<HTMLDivElement>(null);

  // Rule 58: the engine answer arrives seconds after submit, outside CLS's 500ms
  // input-exclusion window, and is taller than the form. The reserve mounts in
  // the SAME commit as the submit (inside the window) and is anchored to the top
  // of the viewport before paint, so the content below is pushed once while
  // excluded and the later answer growth happens below the fold.
  useLayoutEffect(() => {
    if (submitSequence === 0 || !reserveRef.current) return;
    reserveRef.current.scrollIntoView({ behavior: "auto", block: "start" });
  }, [submitSequence]);

  const editQuestion = () => {
    textareaRef.current?.scrollIntoView({ behavior: "auto", block: "center" });
    textareaRef.current?.focus();
  };

  const handleChipClick = (text: string) => {
    if (textareaRef.current) {
      textareaRef.current.value = text;
    }
    questionRef.current = text;
    setHasQuestion(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const question = questionRef.current.trim();
    if (question.length < 5) {
      setError(
        isHi ? "कृपया कम से कम 5 अक्षरों का प्रश्न लिखें।" : "Please enter at least 5 characters."
      );
      return;
    }
    setError(null);
    setLoading(true);
    setResult(null);
    setSubmitSequence((sequence) => sequence + 1);

    try {
      const payload: Record<string, unknown> = {
        question,
        language: locale,
        question_time: new Date().toISOString(),
      };
      if (selectedPlace) {
        payload.latitude = selectedPlace.latitude;
        payload.longitude = selectedPlace.longitude;
        payload.timezone = selectedPlace.timezone;
        payload.question_location = selectedPlace.display ?? selectedPlace.name;
      }

      // apiRequest prepends API_BASE which already ends in /api/v1 — pass the
      // bare path, not /api/v1/prashna (that 404s as /api/v1/api/v1/prashna).
      const data = await apiRequest<PrashnaResult>("/prashna", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setResult(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : isHi
            ? "कुछ गलत हुआ। पुनः प्रयास करें।"
            : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const dirLabels = isHi ? DIRECTION_LABEL_HI : DIRECTION_LABEL;

  return (
    <div data-design-owner="prashna-client">
      <PrashnaForm
        textareaRef={textareaRef}
        hasQuestion={hasQuestion}
        locationText={locationText}
        loading={loading}
        error={error}
        isHi={isHi}
        t={t}
        onChipClick={handleChipClick}
        onQuestionChange={(value) => {
          questionRef.current = value;
          setHasQuestion(Boolean(value.trim()));
        }}
        onLocationChange={setLocationText}
        onPlaceSelect={(place) => {
          setSelectedPlace(place);
          setLocationText(place.display ?? place.name);
        }}
        onSubmit={handleSubmit}
      />
      {submitSequence > 0 && (
        <PrashnaResultReserve ref={reserveRef}>
          {loading && <PrashnaCastingStatus label={t.calculating} />}
          {!loading && !result && error && (
            <PrashnaReserveError message={error} editLabel={t.editQuestion} onEdit={editQuestion} />
          )}
          {result && (
            <div
              aria-live="polite"
              aria-label={isHi ? "प्रश्न कुंडली परिणाम" : "Prashna Kundali result"}
              role="region"
            >
              <PrashnaResultPanel result={result} dirLabels={dirLabels} t={t} />
              <PrashnaFollowUpCTA locale={locale} t={t} getQuestion={() => questionRef.current} />
            </div>
          )}
        </PrashnaResultReserve>
      )}
    </div>
  );
}

function PrashnaForm({
  textareaRef,
  hasQuestion,
  locationText,
  loading,
  error,
  isHi,
  t,
  onChipClick,
  onQuestionChange,
  onLocationChange,
  onPlaceSelect,
  onSubmit,
}: {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  hasQuestion: boolean;
  locationText: string;
  loading: boolean;
  error: string | null;
  isHi: boolean;
  t: PrashnaLabels;
  onChipClick: (text: string) => void;
  onQuestionChange: (value: string) => void;
  onLocationChange: (value: string) => void;
  onPlaceSelect: (place: PlaceResult) => void;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <form className="space-y-5" onSubmit={onSubmit}>
      <div>
        <PrashnaQuickQuestions
          locale={isHi ? "hi" : "en"}
          questionLabel={t.questionLabel}
          onSelect={onChipClick}
        />

        <textarea
          ref={textareaRef}
          id="prashna-question"
          name="question"
          defaultValue=""
          onChange={(event) => onQuestionChange(event.target.value)}
          placeholder={t.questionPlaceholder}
          rows={3}
          required
          aria-describedby={error ? "prashna-error" : undefined}
          aria-invalid={Boolean(error)}
          data-cwv-interaction-target="prashna-question"
          className="w-full resize-none rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-body-small text-text-primary placeholder:text-text-muted focus:border-border-interactive focus:outline-none focus:ring-2 focus:ring-intent-brand"
        />
      </div>

      <div>
        <label
          className="mb-1 block text-body-small font-semibold text-text-primary"
          htmlFor="prashna-location"
        >
          {t.locationLabel}{" "}
          <span className="font-normal text-text-muted">({isHi ? "वैकल्पिक" : "optional"})</span>
        </label>
        <PlaceAutocomplete
          id="prashna-location"
          value={locationText}
          onChange={onLocationChange}
          onSelect={onPlaceSelect}
          placeholder={t.locationPlaceholder}
          describedBy="prashna-location-hint"
          className="input-sacred w-full rounded-lg px-4 py-3 pr-9 text-body-small"
        />
        <p id="prashna-location-hint" className="mt-1 text-caption text-text-muted">
          {t.locationHint}
        </p>
      </div>

      {error && (
        <p
          id="prashna-error"
          className="rounded-lg bg-error/10 px-3 py-2 text-body-small text-error"
          role="alert"
        >
          {error}
        </p>
      )}

      <button
        id="prashna-submit"
        type="submit"
        disabled={loading || !hasQuestion}
        aria-busy={loading}
        className="min-h-11 w-full rounded-lg bg-surface-brand-strong px-4 text-button font-semibold text-text-on-brand transition-colors hover:bg-surface-brand-strong-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-intent-brand disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? t.calculating : t.submitLabel}
      </button>
    </form>
  );
}

function PrashnaResultPanel({
  result,
  dirLabels,
  t,
}: {
  result: PrashnaResult;
  dirLabels: Record<string, DirectionMeta>;
  t: PrashnaLabels;
}) {
  return (
    <div className="mt-8 space-y-5">
      <DirectionCard result={result} dirLabels={dirLabels} t={t} />
      <PredictionSummary result={result} t={t} />
      <HouseAnalysis result={result} t={t} />
    </div>
  );
}

function DirectionCard({
  result,
  dirLabels,
  t,
}: {
  result: PrashnaResult;
  dirLabels: Record<string, DirectionMeta>;
  t: PrashnaLabels;
}) {
  return (
    <div className="rounded-xl border border-border-default bg-surface-brand-subtle p-5">
      <p className="mb-1 text-caption font-semibold uppercase tracking-wide text-text-muted">
        {result.question_category.emoji} {result.question_category.name_en}
      </p>
      <div className="mb-3 flex items-center gap-3">
        <h2
          className={`text-title font-bold ${dirLabels[result.answer_direction]?.textClassName ?? "text-text-primary"}`}
        >
          {dirLabels[result.answer_direction]?.label ?? result.answer_direction}
        </h2>
        <span className="text-body-small text-text-secondary">
          {t.confidenceLabel}: {Math.round(result.confidence_score * 100)}%
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: t.lagnaLabel, value: result.prashna_lagna },
          { label: t.horaLabel, value: result.hora_lord },
          { label: t.moonLabel, value: result.moon_rashi },
          { label: t.significatorLabel, value: result.ruling_planets.join(", ") },
        ].map(({ label, value }) => (
          <ResultFact key={label} label={label} value={value} />
        ))}
      </div>
    </div>
  );
}

function ResultFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-raised p-3">
      <p className="text-eyebrow uppercase tracking-wide text-text-muted">{label}</p>
      <p className="mt-1 text-body-small font-semibold capitalize text-text-primary">{value}</p>
    </div>
  );
}

function PredictionSummary({ result, t }: { result: PrashnaResult; t: PrashnaLabels }) {
  if (!result.prediction_summary) return null;
  return (
    <div className="card-sacred p-5">
      <p className="mb-2 text-body-small font-semibold text-intent-brand">{t.summaryLabel}</p>
      <p className="whitespace-pre-wrap text-body-small leading-relaxed text-text-primary">
        {result.prediction_summary}
      </p>
    </div>
  );
}

function HouseAnalysis({ result, t }: { result: PrashnaResult; t: PrashnaLabels }) {
  if (result.house_analysis.length === 0) return null;
  return (
    <div>
      <p className="mb-3 text-body-small font-semibold text-text-primary">{t.houseAnalysisLabel}</p>
      <div className="space-y-2">
        {result.house_analysis.map((house) => (
          <HouseAnalysisRow key={house.house_number} house={house} />
        ))}
      </div>
    </div>
  );
}

function HouseAnalysisRow({ house }: { house: PrashnaResult["house_analysis"][number] }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border-default bg-surface-raised p-3">
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-caption font-bold ${
          house.is_strong
            ? "bg-surface-brand-strong text-text-on-brand"
            : "bg-surface-warm text-text-muted"
        }`}
      >
        {house.house_number}
      </span>
      <div>
        <p className="text-body-small font-semibold capitalize text-text-primary">{house.sign}</p>
        <p className="text-caption text-text-secondary">{house.significance}</p>
      </div>
    </div>
  );
}
